import { useEffect, useMemo, useRef, useState } from "react";
import { AssistantPicker, type AssistantChoice } from "@/components/assistant/AssistantPicker";
import { ASSISTANT_CHANGED_EVENT } from "@/lib/assistants";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { INTRO_COPY } from "@/i18n/introWizard";
import { RETAIL_SECTORS } from "@/components/SectorOnboardingModal";
import { generateOnboardingAssessment } from "@/lib/onboardingAssessment.functions";
import { createIntroSnapshot, type IntroAssessmentSnapshot } from "@/lib/introAssessmentSnapshot";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import MarkerHighlight from "@/components/MarkerHighlight";
import { toast } from "sonner";
import {
  Crown, Rocket, Settings, MonitorSmartphone, HelpCircle, UserCircle2, Calculator, Briefcase, FileQuestion,
  Construction, Store, Building, Network, Magnet, TrendingUp, Users, Boxes, Maximize2, Compass, Loader2,
  type LucideIcon,
} from "lucide-react";

type Opt = { key: string; es: string; en: string; Icon: LucideIcon };
const UNKNOWN = "__unknown__";
const NOT_REGISTERED = "__not_registered__";
export const INTRO_LANGUAGE = "es" as const;

const ROLES: Opt[] = [
  { key: "ceo", es: "CEO / Propietario", en: "CEO / Owner", Icon: Crown },
  { key: "emprendedor", es: "Emprendedor – Nueva marca", en: "Founder – New brand", Icon: Rocket },
  { key: "operaciones", es: "Director de Operaciones / Tienda", en: "Operations / Store Director", Icon: Settings },
  { key: "digital", es: "Digital / Ecommerce", en: "Digital / Ecommerce", Icon: MonitorSmartphone },
];
const FISCAL: Opt[] = [
  { key: "autonomo_directa", es: "Autónomo – Estimación Directa", en: "Self-employed – Direct estimation", Icon: UserCircle2 },
  { key: "autonomo_modulos", es: "Autónomo – Módulos / Recargo de Equivalencia", en: "Self-employed – Modules / Equivalence Surcharge", Icon: Calculator },
  { key: "sociedad", es: "Sociedad Limitada o S.A.", en: "Limited Company (S.L. / S.A.)", Icon: Briefcase },
];
const LOCATIONS: Opt[] = [
  { key: "proyecto", es: "Próxima apertura / Proyecto", en: "Upcoming opening / Project", Icon: Construction },
  { key: "uno", es: "1 Local", en: "1 Location", Icon: Store },
  { key: "2_5", es: "2 a 5 Locales", en: "2 to 5 Locations", Icon: Building },
  { key: "5_plus", es: "Más de 5 Locales", en: "More than 5 Locations", Icon: Network },
];
const CHALLENGES: Opt[] = [
  { key: "trafico", es: "Tráfico y Clientes", en: "Traffic & Customers", Icon: Magnet },
  { key: "rentabilidad", es: "Rentabilidad y Márgenes", en: "Profitability & Margins", Icon: TrendingUp },
  { key: "equipo", es: "Equipo y Gestión", en: "Team & Management", Icon: Users },
  { key: "compras", es: "Compras y Stock", en: "Purchasing & Stock", Icon: Boxes },
  { key: "expansion", es: "Expansión", en: "Expansion", Icon: Maximize2 },
];

const matchOpt = (opts: Opt[], stored: string) => opts.find((o) => o.key === stored || o.es === stored || o.en === stored);

interface Answers {
  firstName: string; lastName: string; role: string;
  brand: string; noBrand: boolean; sector: string; web: string; noWeb: boolean;
  fiscal: string; locations: string; challenges: string[]; challengeUnknown: boolean; lang: "es" | "en";
}

/** Pasos que se rellenan con "aún no lo sé": se guardan como pendientes de ayuda, no como error. */
export const computePending = (a: Answers): string[] => {
  const p: string[] = [];
  if (a.role === UNKNOWN) p.push("role");
  if (a.noBrand) p.push("brand");
  if (a.sector === UNKNOWN) p.push("sector");
  if (a.noWeb) p.push("website");
  if (a.fiscal === UNKNOWN || a.fiscal === NOT_REGISTERED) p.push("fiscal");
  if (a.locations === UNKNOWN) p.push("locations");
  if (a.challengeUnknown) p.push("challenge");
  return p;
};

/** Regla de avance: cada pregunta del paso necesita una elección (las de "no lo sé" cuentan). */
export const canContinueStep = (step: number, a: Answers): boolean => {
  if (step === 1) return !!a.firstName.trim() && !!a.lastName.trim() && !!a.role;
  if (step === 2) return (a.noBrand || !!a.brand.trim()) && !!a.sector && (a.noWeb || !!a.web.trim());
  if (step === 3) return !!a.fiscal && !!a.locations;
  if (step === 4) return a.challengeUnknown || a.challenges.length > 0;
  return true;
};

const Tile = ({ Icon, label, selected, onClick }: { Icon: LucideIcon; label: string; selected: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={cn(
      "flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-center transition-all sm:min-h-[80px] sm:px-3",
      selected ? "border-[#CA8A04] bg-[#FACC15] shadow-md" : "border-border bg-card hover:border-foreground/30",
    )}
  >
    <Icon className={cn("h-[18px] w-[18px]", selected ? "text-gray-900" : "text-foreground/55")} strokeWidth={selected ? 2 : 1.5} />
    <span className={cn("font-body text-[12px] leading-tight sm:text-[13px]", selected ? "font-bold text-gray-900" : "text-foreground/75")}>{label}</span>
  </button>
);

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <p className="font-body text-sm font-medium text-foreground">{label}</p>
    {children}
  </div>
);

interface IntroWizardProps {
  open: boolean;
  /** Vista previa del administrador: no lee ni escribe el perfil. */
  preview?: boolean;
  onClosePreview?: () => void;
  onFinished?: () => void;
}

const IntroWizard = ({ open, preview = false, onClosePreview, onFinished }: IntroWizardProps) => {
  const { lang: uiLang, setLang } = useLanguage();
  const [step, setStep] = useState(1);
  const [choosing, setChoosing] = useState(false);
  const [assistant, setAssistant] = useState<AssistantChoice>({ gender: "female", id: null, name: "" });
  const [ready, setReady] = useState(preview);
  const [saving, setSaving] = useState(false);
  const assessmentInFlight = useRef(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<IntroAssessmentSnapshot | null>(null);
  const [assessmentError, setAssessmentError] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  useEffect(() => {
    if (step !== 5 || assessment || assessmentError) { setLoadPct(assessment ? 100 : 0); return; }
    const t0 = Date.now();
    setLoadPct(0);
    // Avanza hacia el 95 % en ~6 s (tiempo típico de la IA) y se frena después.
    const id = window.setInterval(() => {
      const s = (Date.now() - t0) / 1000;
      setLoadPct(Math.min(95, 95 * (1 - Math.exp(-s / 3))));
    }, 200);
    return () => window.clearInterval(id);
  }, [step, assessment, assessmentError]);
  const [a, setA] = useState<Answers>({
    firstName: "", lastName: "", role: "", brand: "", noBrand: false, sector: "", web: "", noWeb: false,
    fiscal: "", locations: "", challenges: [], challengeUnknown: false, lang: INTRO_LANGUAGE,
  });
  const c = INTRO_COPY[a.lang];
  const L = (o: Opt) => (a.lang === "en" ? o.en : o.es);
  const set = (patch: Partial<Answers>) => setA((prev) => ({ ...prev, ...patch }));

  // Carga y reanudación: datos del agente (perfil) → Google → vacío.
  useEffect(() => {
    if (!open) return;
    if (preview) { setStep(1); setAssessment(null); setReady(true); return; }
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || cancelled) return;
      setUserId(session.user.id);
      const meta = (session.user.user_metadata ?? {}) as Record<string, string>;
      const { data: p } = await supabase.from("profiles")
        .select("first_name, last_name, role, company_name, retail_sector, website_url, no_website, tax_regime, physical_presence, challenge, preferred_language, onboarding_step, onboarding_pending, onboarding_assessment")
        .eq("user_id", session.user.id).maybeSingle();
      if (cancelled) return;
      const pending = new Set<string>(p?.onboarding_pending ?? []);
      const full = (meta["full_name"] || meta["name"] || "").trim();
      const challenges = (p?.challenge ?? "").split(",").map((s) => s.trim()).filter(Boolean)
        .map((s) => matchOpt(CHALLENGES, s)?.es).filter(Boolean) as string[];
      setA({
        firstName: p?.first_name || meta["given_name"] || full.split(/\s+/)[0] || "",
        lastName: p?.last_name || meta["family_name"] || full.split(/\s+/).slice(1).join(" ") || "",
        role: (matchOpt(ROLES, p?.role ?? "")?.es ?? ""),
        brand: p?.company_name ?? "",
        noBrand: pending.has("brand"),
        sector: (RETAIL_SECTORS.some((s) => s.key === p?.retail_sector) ? p?.retail_sector ?? "" : ""),
        web: p?.website_url ?? "",
        noWeb: pending.has("website") || !!p?.no_website,
        fiscal: pending.has("fiscal") ? (p?.tax_regime === "not_registered" ? NOT_REGISTERED : "") : (matchOpt(FISCAL, p?.tax_regime ?? "")?.key ?? ""),
        locations: (matchOpt(LOCATIONS, p?.physical_presence ?? "")?.es ?? ""),
        challenges,
        challengeUnknown: pending.has("challenge"),
        lang: INTRO_LANGUAGE,
      });
      const saved = p?.onboarding_assessment as Partial<IntroAssessmentSnapshot> | null;
      if (saved?.reading?.trim() && saved.steps?.length === 3) {
        const snapshot = createIntroSnapshot({ reading: saved.reading, steps: saved.steps }, saved.pending ?? p?.onboarding_pending ?? [], INTRO_COPY[saved.lang ?? INTRO_LANGUAGE].pending, saved.lang ?? INTRO_LANGUAGE);
        setAssessment({ ...snapshot, helpLabels: saved.helpLabels ?? snapshot.helpLabels });
      }
      setStep(Math.min(Math.max(p?.onboarding_step ?? 1, 1), 5));
      setReady(true);
    })();
    return () => { cancelled = true; };
  }, [open, preview]);

  const pending = useMemo(() => computePending(a), [a]);

  const stepPayload = (s: number): Record<string, unknown> => {
    if (s === 1) return { first_name: a.firstName.trim(), last_name: a.lastName.trim(), role: a.role === UNKNOWN ? "" : a.role };
    if (s === 2) return {
      company_name: a.noBrand ? "" : a.brand.trim(),
      retail_sector: a.sector === UNKNOWN ? "" : a.sector,
      website_url: a.noWeb ? "" : a.web.trim(),
      no_website: a.noWeb,
    };
    if (s === 3) return {
      tax_regime: a.fiscal === UNKNOWN ? null : a.fiscal === NOT_REGISTERED ? "not_registered" : a.fiscal,
      physical_presence: a.locations === UNKNOWN ? "" : a.locations,
    };
    return { challenge: a.challengeUnknown ? "" : a.challenges.join(", "), preferred_language: a.lang };
  };

  const loadAssessment = async () => {
    if (assessmentInFlight.current) return;
    assessmentInFlight.current = true;
    setAssessment(null);
    setAssessmentError(false);
    try {
      const sector = RETAIL_SECTORS.find((s) => s.key === a.sector);
      const r = await generateOnboardingAssessment({ data: {
        lang: a.lang, firstName: a.firstName, role: a.role === UNKNOWN ? c.roleUnknown : a.role,
        brand: a.noBrand ? c.noBrand : a.brand, sector: sector ? (a.lang === "en" ? sector.labelEn : sector.label) : c.sectorUnknown,
        website: a.noWeb ? c.noWeb : a.web,
        fiscal: a.fiscal === UNKNOWN ? c.unknown : a.fiscal === NOT_REGISTERED ? c.notRegistered : (FISCAL.find((f) => f.key === a.fiscal)?.es ?? ""),
        locations: a.locations === UNKNOWN ? c.unknown : a.locations,
        challenges: a.challengeUnknown ? [c.challengeUnknown] : a.challenges,
        pending: pending.map((k) => c.pending[k] ?? k),
      } });
      if (r.status !== "ai" || !r.reading.trim() || r.steps.length !== 3) {
        setAssessmentError(true);
        return;
      }
      const snapshot = createIntroSnapshot({ reading: r.reading, steps: r.steps }, pending, c.pending, a.lang);
      if (!preview && userId) {
        const { data, error } = await supabase.from("profiles").update({ onboarding_assessment: snapshot as never }).eq("user_id", userId).select("user_id").single();
        if (error || !data) { toast.error(c.errSave); setAssessmentError(true); return; }
      }
      setAssessment(snapshot);
    } catch (e) {
      console.error(e);
      setAssessmentError(true);
    } finally {
      assessmentInFlight.current = false;
    }
  };

  // Al reanudar en la valoración sin texto guardado, se genera.
  useEffect(() => {
    if (ready && step === 5 && !assessment) void loadAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, step]);

  const handleContinue = async () => {
    if (!canContinueStep(step, a)) return;
    if (!preview && userId) {
      setSaving(true);
      const { error } = await supabase.from("profiles")
        .update({ ...stepPayload(step), onboarding_step: step + 1, onboarding_pending: pending } as never)
        .eq("user_id", userId);
      setSaving(false);
      if (error) { console.error(error); toast.error(c.errSave); return; }
    }
    if (step === 4) {
      if (a.lang !== uiLang) setLang(a.lang);
      setStep(5);
      void loadAssessment();
      return;
    }
    setStep(step + 1);
  };

  const handleEnter = async () => {
    if (preview) { if (!choosing) { setChoosing(true); return; } onClosePreview?.(); return; }
    if (!userId || !assessment?.reading.trim()) return;
    if (!choosing) { setChoosing(true); return; }
    if (!assistant.id) return;
    setSaving(true);
    const { data, error } = await supabase.from("profiles").update({ onboarding_completed: true, onboarding_step: 5, onboarding_assessment: assessment as never, assistant_gender: assistant.gender, assistant_id: assistant.id, assistant_name: assistant.name.trim() || null }).eq("user_id", userId).select("user_id").single();
    setSaving(false);
    if (error || !data) { console.error(error); toast.error(c.errSave); return; }
    window.dispatchEvent(new Event(ASSISTANT_CHANGED_EVENT));
    trackEvent("onboarding_complete", { retail_sector: a.sector, role: a.role });
    onFinished?.();
  };

  const toggleChallenge = (o: Opt) => {
    if (a.challenges.includes(o.es)) { set({ challenges: a.challenges.filter((x) => x !== o.es) }); return; }
    const next = a.challenges.length >= 2 ? [...a.challenges.slice(1), o.es] : [...a.challenges, o.es];
    set({ challenges: next, challengeUnknown: false });
  };

  const titles = [c.s1Title, c.s2Title, c.s3Title, c.s4Title, c.aTitle];
  const subs = [c.s1Sub, c.s2Sub, c.s3Sub, c.s4Sub, ""];
  const progress = Math.min(step, 4) / 4;
  const canGo = canContinueStep(step, a);
  const grid = "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5";

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="flex h-auto max-h-[82vh] w-[92vw] max-w-3xl min-h-0 flex-col gap-0 overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-2xl sm:max-h-[88vh] [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <div className="shrink-0 border-b border-border px-4 pb-3 pt-4 sm:px-8 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {step <= 4 ? c.stepOf.replace("{s}", String(step)) : (
                <span className="font-display text-sm font-bold normal-case tracking-tight text-foreground">
                  RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
                </span>
              )}
            </p>
            {preview && (
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClosePreview} className="rounded-full border border-border px-3 py-1 font-body text-xs hover:bg-secondary">{c.closePreview}</button>
              </div>
            )}
          </div>
          <DialogTitle className="mt-1.5 font-display text-xl font-bold text-foreground sm:text-2xl">{choosing ? ((a.lang === "es") ? "Configura tu asistente" : "Set up your assistant") : titles[step - 1]}</DialogTitle>
          <DialogDescription className="mt-1 font-body text-xs text-muted-foreground sm:text-sm">{choosing ? ((a.lang === "es") ? "Elige quién te acompañará en el panel. Podrás cambiarlo cuando quieras en Mi Perfil." : "Choose who will guide you in the panel. You can change it any time in My Profile.") : (subs[step - 1] || " ")}</DialogDescription>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#FFE143]/35">
            <div className="h-full bg-[#F5C800] transition-all duration-500" style={{ width: `${(step === 5 && !assessment && !assessmentError ? loadPct / 100 : progress) * 100}%` }} />
          </div>
          {step === 5 && !assessment && !assessmentError && (
            <p className="mt-1 text-right font-body text-xs tabular-nums text-muted-foreground">{Math.round(loadPct)} %</p>
          )}
        </div>

        <div key={choosing ? "assistant" : step} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-8 sm:py-5">
          {choosing ? (
            <AssistantPicker value={assistant} onChange={setAssistant} lang={(a.lang === "es") ? "es" : "en"} />
          ) : !ready ? (
            <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : step === 1 ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={c.firstName}><Input value={a.firstName} onChange={(e) => set({ firstName: e.target.value })} /></Field>
                <Field label={c.lastName}><Input value={a.lastName} onChange={(e) => set({ lastName: e.target.value })} /></Field>
              </div>
              <Field label={c.roleLabel}>
                <div className={grid}>
                  {ROLES.map((o) => <Tile key={o.key} Icon={o.Icon} label={L(o)} selected={a.role === o.es} onClick={() => set({ role: o.es })} />)}
                </div>
              </Field>
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={c.brand}>
                  <Input value={a.brand} disabled={a.noBrand} placeholder={c.brandPh} onChange={(e) => set({ brand: e.target.value })} />
                  <label className="flex cursor-pointer items-center gap-2 font-body text-xs text-muted-foreground">
                    <Checkbox checked={a.noBrand} onCheckedChange={(v) => set({ noBrand: !!v, ...(v ? { brand: "" } : {}) })} />
                    {c.noBrand}
                  </label>
                </Field>
                <Field label={c.web}>
                  <Input type="url" value={a.web} disabled={a.noWeb} placeholder={c.webPh} onChange={(e) => set({ web: e.target.value })} />
                  <label className="flex cursor-pointer items-center gap-2 font-body text-xs text-muted-foreground">
                    <Checkbox checked={a.noWeb} onCheckedChange={(v) => set({ noWeb: !!v, ...(v ? { web: "" } : {}) })} />
                    {c.noWeb}
                  </label>
                </Field>
              </div>
              <Field label={c.sector}>
                <div className={grid}>
                  {RETAIL_SECTORS.map((s) => (
                    <Tile key={s.key} Icon={s.Icon} label={a.lang === "en" ? s.labelEn : s.label} selected={a.sector === s.key} onClick={() => set({ sector: s.key })} />
                  ))}
                </div>
              </Field>
            </div>
          ) : step === 3 ? (
            <div className="space-y-4">
              <Field label={c.fiscal}>
                <div className={grid}>
                  {FISCAL.map((o) => <Tile key={o.key} Icon={o.Icon} label={L(o)} selected={a.fiscal === o.key} onClick={() => set({ fiscal: o.key })} />)}
                  <Tile Icon={FileQuestion} label={c.notRegistered} selected={a.fiscal === NOT_REGISTERED} onClick={() => set({ fiscal: NOT_REGISTERED })} />
                </div>
              </Field>
              <Field label={c.locations}>
                <div className={grid}>
                  {LOCATIONS.map((o) => <Tile key={o.key} Icon={o.Icon} label={L(o)} selected={a.locations === o.es} onClick={() => set({ locations: o.es })} />)}
                </div>
              </Field>
            </div>
          ) : step === 4 ? (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-right font-body text-xs text-muted-foreground">{a.challengeUnknown ? 0 : a.challenges.length}/2</p>
                <div className={grid}>
                  {CHALLENGES.map((o) => <Tile key={o.key} Icon={o.Icon} label={L(o)} selected={!a.challengeUnknown && a.challenges.includes(o.es)} onClick={() => toggleChallenge(o)} />)}
                  <Tile Icon={Compass} label={c.challengeUnknown} selected={a.challengeUnknown} onClick={() => set({ challengeUnknown: !a.challengeUnknown, challenges: [] })} />
                </div>
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-6">
              {!assessment && !assessmentError ? (
                <div className="flex flex-col items-center gap-4 rounded-xl border border-border p-8 text-center">
                  <span className="animate-pulse font-display text-2xl font-bold tracking-tight text-foreground">
                    RETAIL<MarkerHighlight height="0.78em" className="px-[0.06em] text-foreground">LEMON</MarkerHighlight>
                  </span>
                  <p className="font-body text-sm text-muted-foreground">{c.aLoading}</p>
                </div>
              ) : assessmentError ? (
                <div className="space-y-4 rounded-xl border border-border p-6 text-center">
                  <p className="font-body text-sm text-muted-foreground">{c.aError}</p>
                  <button type="button" onClick={() => void loadAssessment()} className="rounded-xl border border-foreground/80 px-5 py-2.5 font-body text-sm font-semibold hover:bg-secondary">
                    {c.aRetry}
                  </button>
                </div>
              ) : (
                <p className="font-body text-base leading-relaxed text-foreground">{assessment?.reading}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-5 py-4 sm:px-10">
          {step > 1 && step <= 4 ? (
            <button type="button" onClick={() => setStep(step - 1)} className="rounded-xl border border-foreground/80 px-5 py-2.5 font-body text-sm hover:bg-secondary">{c.back}</button>
          ) : <span />}
          {step <= 4 ? (
            <button type="button" onClick={handleContinue} disabled={!canGo || saving || !ready}
              className="rounded-xl bg-gray-900 px-6 py-3 font-body text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-30">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : step === 4 ? c.seeAssessment : c.continue}
            </button>
          ) : (
            <button type="button" onClick={handleEnter} disabled={!assessment || saving || (choosing && !assistant.id)}
              className="rounded-xl bg-gray-900 px-6 py-3 font-body text-sm font-semibold text-white disabled:opacity-30">
               {choosing ? ((a.lang === "es") ? "Empezar" : "Start") : c.enterPanel}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IntroWizard;
